import {
  defineDoctype,
  type DoctypeCatalogMetadata,
  type DoctypeDefinition,
} from "@kubdee/sdk";
import { connectorCredentials } from "./schemas/connectors-credentials";
import { connectorInstances } from "./schemas/connectors-instances";
import { connectorOauthStates } from "./schemas/connectors-oauth-states";
import { connectorRateLimits } from "./schemas/connectors-rate-limits";
import { connectorSubscriptions } from "./schemas/connectors-subscriptions";
import { connectorSyncJobs } from "./schemas/connectors-sync-jobs";
import { connectorTypes } from "./schemas/connectors-types";
import { connectorWebhookEvents } from "./schemas/connectors-webhook-events";

// ============================================================================
// Connectors module - registered doctypes.
//
// Phase 0F of #644 keeps this metadata-only. OAuth, webhook, vault, scheduler,
// and rate-limit writes stay owned by the connectors routes/services/DOs.
// ============================================================================

const READ_ONLY_REFERENCE_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: false,
    isExportable: false,
    isReportable: false,
  },
};

const SENSITIVE_SYSTEM_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: false,
    isExportable: false,
    isReportable: false,
  },
};

const OPERATIONAL_LOG_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isReadOnly: true,
    isSearchable: false,
    isExportable: false,
    isReportable: false,
  },
};

export const connectorTypeDoctype = defineDoctype({
  id: "connector_type",
  moduleId: "connectors",
  table: connectorTypes,
  label: "ประเภทการเชื่อมต่อ",
  icon: "Plug",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "system",
  catalog: READ_ONLY_REFERENCE_CATALOG,
  displayField: "name",
  searchFields: ["slug", "name", "capabilities"],
  columnLabels: {
    slug: "Slug",
    name: "ชื่อ Provider",
    icon: "ไอคอน",
    icon_url: "URL ไอคอน",
    capabilities: "ความสามารถ",
    oauth_config: "ตั้งค่า OAuth",
    webhook_config: "ตั้งค่า Webhook",
    is_active: "เปิดใช้งาน",
  },
});

export const connectorInstanceDoctype = defineDoctype({
  id: "connector_instance",
  moduleId: "connectors",
  table: connectorInstances,
  label: "การเชื่อมต่อที่ใช้งาน",
  icon: "Cable",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: READ_ONLY_REFERENCE_CATALOG,
  displayField: "display_name",
  searchFields: ["display_name", "status", "capability_primary"],
  columnLabels: {
    company_id: "บริษัท",
    type_id: "ประเภทการเชื่อมต่อ",
    display_name: "ชื่อที่แสดง",
    status: "สถานะ",
    capability_primary: "ความสามารถหลัก",
    config: "ตั้งค่า",
    metadata: "ข้อมูลเพิ่มเติม",
    default_ai_agent_id: "AI Agent เริ่มต้น",
    last_sync_at: "ซิงก์ล่าสุดเมื่อ",
    last_error: "ข้อผิดพลาดล่าสุด",
    disabled_reason: "เหตุผลที่ปิดใช้งาน",
  },
});

export const connectorCredentialDoctype = defineDoctype({
  id: "connector_credential",
  moduleId: "connectors",
  table: connectorCredentials,
  label: "Credential การเชื่อมต่อ",
  icon: "KeyRound",
  features: ["permissions"],
  tier: 2,
  kind: "system",
  catalog: SENSITIVE_SYSTEM_CATALOG,
  displayField: "id",
  columnLabels: {
    instance_id: "การเชื่อมต่อ",
    access_token_enc: "Access Token (Encrypted)",
    refresh_token_enc: "Refresh Token (Encrypted)",
    expires_at: "หมดอายุเมื่อ",
    refresh_expires_at: "Refresh หมดอายุเมื่อ",
    scope: "Scope",
    token_type: "ประเภท Token",
    encryption_key_id: "รหัสกุญแจเข้ารหัส",
    rotated_at: "หมุนกุญแจเมื่อ",
  },
});

export const connectorOauthStateDoctype = defineDoctype({
  id: "connector_oauth_state",
  moduleId: "connectors",
  table: connectorOauthStates,
  label: "OAuth State",
  icon: "ShieldCheck",
  features: ["permissions"],
  tier: 2,
  kind: "system",
  catalog: SENSITIVE_SYSTEM_CATALOG,
  displayField: "state",
  columnLabels: {
    company_id: "บริษัท",
    type_id: "ประเภทการเชื่อมต่อ",
    state: "State",
    code_verifier: "Code Verifier",
    redirect_after: "Redirect หลังเชื่อมต่อ",
    user_id: "ผู้ใช้",
    expires_at: "หมดอายุเมื่อ",
  },
});

export const connectorWebhookEventDoctype = defineDoctype({
  id: "connector_webhook_event",
  moduleId: "connectors",
  table: connectorWebhookEvents,
  label: "Webhook Event",
  icon: "Webhook",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "ledger",
  catalog: OPERATIONAL_LOG_CATALOG,
  displayField: "event_id",
  searchFields: ["event_id", "event_type", "processing_status"],
  columnLabels: {
    instance_id: "การเชื่อมต่อ",
    event_id: "Event ID",
    event_type: "ประเภท Event",
    signature_valid: "ลายเซ็นถูกต้อง",
    payload_hash: "Payload Hash",
    payload: "Payload",
    received_at: "รับเมื่อ",
    processed_at: "ประมวลผลเมื่อ",
    processing_status: "สถานะประมวลผล",
    error: "ข้อผิดพลาด",
    retry_count: "จำนวน Retry",
  },
});

export const connectorSyncJobDoctype = defineDoctype({
  id: "connector_sync_job",
  moduleId: "connectors",
  table: connectorSyncJobs,
  label: "Sync Job",
  icon: "RefreshCw",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "system",
  catalog: OPERATIONAL_LOG_CATALOG,
  displayField: "job_type",
  searchFields: ["job_type", "status", "error"],
  columnLabels: {
    instance_id: "การเชื่อมต่อ",
    job_type: "ประเภท Job",
    status: "สถานะ",
    scheduled_at: "กำหนดรันเมื่อ",
    started_at: "เริ่มเมื่อ",
    completed_at: "เสร็จเมื่อ",
    error: "ข้อผิดพลาด",
    attempts: "จำนวนครั้งที่ลอง",
    max_attempts: "จำนวนครั้งสูงสุด",
    payload: "Payload",
    result: "ผลลัพธ์",
  },
});

export const connectorRateLimitDoctype = defineDoctype({
  id: "connector_rate_limit",
  moduleId: "connectors",
  table: connectorRateLimits,
  label: "Rate Limit Counter",
  icon: "Gauge",
  features: ["permissions"],
  tier: 2,
  kind: "system",
  catalog: SENSITIVE_SYSTEM_CATALOG,
  displayField: "endpoint",
  columnLabels: {
    instance_id: "การเชื่อมต่อ",
    endpoint: "Endpoint",
    window_start: "เริ่มรอบเวลา",
    window_ms: "ความยาวรอบเวลา (ms)",
    request_count: "จำนวน Request",
    max_per_window: "จำนวนสูงสุดต่อรอบ",
    updated_at: "อัปเดตเมื่อ",
  },
});

export const connectorSubscriptionDoctype = defineDoctype({
  id: "connector_subscription",
  moduleId: "connectors",
  table: connectorSubscriptions,
  label: "Event Subscription",
  icon: "RadioTower",
  features: ["permissions", "list_view"],
  tier: 2,
  kind: "junction",
  catalog: READ_ONLY_REFERENCE_CATALOG,
  displayField: "event_name",
  searchFields: ["module_id", "capability", "event_name", "handler_ref"],
  columnLabels: {
    instance_id: "การเชื่อมต่อ",
    module_id: "โมดูล",
    capability: "ความสามารถ",
    event_name: "ชื่อ Event",
    handler_ref: "Handler",
    active: "เปิดใช้งาน",
  },
});

export const connectorsDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  connectorTypeDoctype,
  connectorInstanceDoctype,
  connectorCredentialDoctype,
  connectorOauthStateDoctype,
  connectorWebhookEventDoctype,
  connectorSyncJobDoctype,
  connectorRateLimitDoctype,
  connectorSubscriptionDoctype,
]);
